import { useEffect, useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHead } from "@/pages/app/AppShell";
import { api, errMsg, money } from "@/lib/api";

const STAGES = ["Prospect", "Contacted", "Meeting", "Proposal", "Negotiation", "Contract", "Won"];

function DealCard({ d, onMove, onDelete }) {
  return (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("id", d.id)} data-testid={`deal-card-${d.id}`}
      className="group rounded-xl border border-[#2A1240]/10 bg-[#FBE9F3] p-4 cursor-grab active:cursor-grabbing hover:border-[#E0218A]/40 transition-colors">
      <div className="flex items-start gap-2">
        <GripVertical className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#2A1240] leading-snug">{d.title}</p>
          {d.partner_name && <p className="text-xs text-slate-400 mt-1">{d.partner_name}</p>}
          <p className="mt-2 font-mono text-sm text-[#8E2BC4]">{money(d.value)}</p>
        </div>
        <button onClick={() => onDelete(d.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-300" aria-label="Delete deal" data-testid={`deal-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <select value={d.stage} onChange={(e) => onMove(d.id, e.target.value)} className="mt-3 w-full rounded-lg bg-[#FFFFFF] border border-[#2A1240]/10 px-2 py-1.5 text-xs text-slate-200" data-testid={`deal-stage-select-${d.id}`}>
        {STAGES.map((s) => <option key={s}>{s}</option>)}
      </select>
    </div>
  );
}

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", partner_name: "", value: "", stage: "Prospect", notes: "" });
  const load = () => api.get("/deals").then((r) => setDeals(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const move = async (id, stage) => {
    setDeals((ds) => ds.map((d) => (d.id === id ? { ...d, stage } : d)));
    try {
      await api.patch(`/deals/${id}`, { stage });
      if (stage === "Won") toast.success("Deal won — congratulations!");
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };
  const del = async (id) => {
    await api.delete(`/deals/${id}`).catch(() => {});
    load();
  };
  const create = async () => {
    try {
      await api.post("/deals", { ...f, value: Number(f.value || 0) });
      setOpen(false);
      setF({ title: "", partner_name: "", value: "", stage: "Prospect", notes: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const total = deals.filter((d) => d.stage !== "Won").reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div data-testid="pipeline-page">
      <PageHead kicker={`${deals.length} deals · ${money(total)} open value`} title="Pipeline">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="pipeline-add-deal"><Plus className="h-4 w-4" />Add deal</button>
      </PageHead>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-4 -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="pipeline-board">
        {STAGES.map((s) => {
          const col = deals.filter((d) => d.stage === s);
          return (
            <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={(e) => move(e.dataTransfer.getData("id"), s)}
              className="w-72 shrink-0 rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-3" data-testid={`pipeline-column-${s.toLowerCase()}`}>
              <div className="flex items-center justify-between px-1 pb-3">
                <p className={`text-sm font-semibold ${s === "Won" ? "text-emerald-300" : "text-[#2A1240]"}`}>{s}</p>
                <span className="text-xs font-mono text-slate-400">{col.length} · {money(col.reduce((a, d) => a + (d.value || 0), 0))}</span>
              </div>
              <div className="space-y-3 min-h-[120px]">
                {col.map((d) => <DealCard key={d.id} d={d} onMove={move} onDelete={del} />)}
                {!col.length && <p className="text-xs text-slate-500 text-center py-8 border border-dashed border-[#2A1240]/10 rounded-xl">Drop deals here</p>}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="deal-dialog">
          <DialogTitle className="font-display text-2xl font-normal">New deal</DialogTitle>
          <input className="field" placeholder="Deal title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="deal-title-input" />
          <input className="field" placeholder="Partner name" value={f.partner_name} onChange={(e) => setF({ ...f, partner_name: e.target.value })} data-testid="deal-partner-input" />
          <div className="grid grid-cols-2 gap-3">
            <input className="field" type="number" placeholder="Value ($)" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="deal-value-input" />
            <select className="field" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} data-testid="deal-stage-input">{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
          </div>
          <textarea className="field" rows={3} placeholder="Notes / follow-ups" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="deal-notes-input" />
          <button className="btn-gold" disabled={!f.title} onClick={create} data-testid="deal-save-button">Save deal</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
